import { useState } from "react";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useStorageStatus } from "../hooks/bindings.ts";
import { Frame, PrimaryButton, Screen } from "../components/Shell.tsx";
import styles from "../design-system/ui.module.css";

export function RecoveryScreen() {
  const { platform, store } = useAppServices();
  const status = useStorageStatus();
  const [message, setMessage] = useState<string>();
  const raw = "raw" in status ? status.raw : null;
  const retryable =
    status.kind === "unavailable" ||
    status.kind === "invalid" ||
    status.kind === "migration_failed";
  const canExportOriginal = raw != null && raw !== "";
  const canExportPreMigration =
    (status.kind === "migration_failed" || status.kind === "ready" || status.kind === "empty") &&
    Boolean("preMigrationCopy" in status && status.preMigrationCopy);

  async function exportOriginal() {
    const bytes = await store.exportRaw();
    if (bytes == null) {
      setMessage("There is no original file to export.");
      return;
    }
    platform.download.save("vocably-recovery.json", bytes, "application/json");
  }

  async function exportPreMigration() {
    const bytes = await store.exportPreMigrationCopy();
    if (bytes == null) {
      setMessage("There is no pre-migration copy to export.");
      return;
    }
    platform.download.save("vocably-pre-migration.json", bytes, "application/json");
  }

  return (
    <Frame>
      <Screen withNav={false}>
        <header className={styles.pageHead}>
          <h1>Vocabulary could not be opened</h1>
          <p>{statusMessage(status)} The original saved data has not been replaced with an empty vocabulary.</p>
        </header>
        <div className={styles.stack}>
          {canExportOriginal ? (
            <PrimaryButton onClick={() => void exportOriginal()}>Export the original data</PrimaryButton>
          ) : null}
          {retryable ? (
            <PrimaryButton secondary onClick={() => void store.reload()}>
              Try again
            </PrimaryButton>
          ) : null}
          {canExportPreMigration ? (
            <PrimaryButton secondary onClick={() => void exportPreMigration()}>
              Export the pre-migration copy
            </PrimaryButton>
          ) : null}
        </div>
        {message ? <p className={styles.banner}>{message}</p> : null}
      </Screen>
    </Frame>
  );
}

function statusMessage(status: ReturnType<typeof useStorageStatus>): string {
  switch (status.kind) {
    case "unavailable":
      return status.message;
    case "invalid":
      return status.message;
    case "unsupported":
      return status.message;
    case "migration_failed":
      return status.message;
    case "migration_blocked":
      return status.message;
    default:
      return "Saved vocabulary is not available.";
  }
}
